import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Mail, Building2, Calendar, LogOut, Check,
  UserCircle, ShieldCheck, BadgeCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './AccountPage.css';

export default function AccountPage() {
  const { user, supabase, signOut } = useAuth();
  const navigate = useNavigate();

  const meta = user?.user_metadata || {};
  const [tab, setTab] = useState('profile');

  // Profile form
  const [name, setName]       = useState(meta.name || '');
  const [company, setCompany] = useState(meta.company || '');
  const [busy, setBusy]       = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState('');

  // Password form
  const [newPass, setNewPass]   = useState('');
  const [confirm, setConfirm]   = useState('');
  const [passBusy, setPassBusy] = useState(false);
  const [passErr, setPassErr]   = useState('');
  const [passOk, setPassOk]     = useState(false);

  const initials = (meta.name || user?.email || '?')
    .split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();

  const joined = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setSaved(false);
    const { error } = await supabase.auth.updateUser({
      data: { name, company, profile_complete: true },
    });
    setBusy(false);
    if (error) return setError(error.message);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPassOk(false); setPassErr('');
    if (newPass.length < 6) return setPassErr('Password must be at least 6 characters.');
    if (newPass !== confirm) return setPassErr('Passwords do not match.');
    setPassBusy(true);
    const { error } = await supabase.auth.updateUser({ password: newPass });
    setPassBusy(false);
    if (error) return setPassErr(error.message);
    setNewPass(''); setConfirm('');
    setPassOk(true);
    setTimeout(() => setPassOk(false), 3000);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="account-page">
      {/* ── Hero ── */}
      <div className="account-hero">
        <div className="account-hero-inner">
          <div className="account-avatar">{initials}</div>
          <div className="account-hero-text">
            <h1>{meta.name || 'Your Account'}</h1>
            <p className="account-hero-email"><Mail size={14} /> {user?.email}</p>
            <div className="account-hero-badges">
              <span className="account-badge"><BadgeCheck size={13} /> Verified Account</span>
              <span className="account-badge account-badge--muted"><Calendar size={13} /> Joined {joined}</span>
            </div>
          </div>
          <button className="account-signout" onClick={handleSignOut}>
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="account-layout">
        {/* Sidebar */}
        <aside className="account-sidebar">
          <button
            className={`account-tab ${tab === 'profile' ? 'active' : ''}`}
            onClick={() => setTab('profile')}
          >
            <UserCircle size={18} /> Profile Information
          </button>
          <button
            className={`account-tab ${tab === 'security' ? 'active' : ''}`}
            onClick={() => setTab('security')}
          >
            <ShieldCheck size={18} /> Security & Password
          </button>
        </aside>

        {/* Content */}
        <div className="account-content">
          {tab === 'profile' && (
            <>
              {/* Quick overview */}
              <div className="account-overview">
                <div className="account-ov"><div className="ov-icon"><User size={17} /></div><div><p className="ov-label">Name</p><p className="ov-value">{name || '—'}</p></div></div>
                <div className="account-ov"><div className="ov-icon"><Building2 size={17} /></div><div><p className="ov-label">Company</p><p className="ov-value">{company || '—'}</p></div></div>
                <div className="account-ov"><div className="ov-icon"><Mail size={17} /></div><div><p className="ov-label">Email</p><p className="ov-value ov-value--email">{user?.email}</p></div></div>
              </div>

              <div className="account-card">
                <div className="account-card-head">
                  <h2>Profile Information</h2>
                  <p>Update your name and company details.</p>
                </div>
                {error && <div className="account-msg account-msg--err">{error}</div>}
                <form onSubmit={saveProfile} className="account-form">
                  <div className="account-field">
                    <label>Full Name</label>
                    <input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" />
                  </div>
                  <div className="account-field">
                    <label>Company Name</label>
                    <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Your company" />
                  </div>
                  <div className="account-field">
                    <label>Email Address</label>
                    <input value={user?.email || ''} disabled className="account-input--locked" />
                    <span className="account-hint">Your email is used to sign in and can't be changed here.</span>
                  </div>
                  <button type="submit" className={`account-save ${saved ? 'is-saved' : ''}`} disabled={busy}>
                    {saved ? <><Check size={16} /> Saved</> : busy ? 'Saving…' : 'Save Changes'}
                  </button>
                </form>
              </div>
            </>
          )}

          {tab === 'security' && (
            <div className="account-card">
              <div className="account-card-head">
                <h2>Security &amp; Password</h2>
                <p>Set a new password for your account. Use at least 6 characters.</p>
              </div>
              {passErr && <div className="account-msg account-msg--err">{passErr}</div>}
              {passOk && <div className="account-msg account-msg--ok"><Check size={15} /> Password updated successfully.</div>}
              <form onSubmit={changePassword} className="account-form">
                <div className="account-field">
                  <label>New Password</label>
                  <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Min 6 characters" />
                </div>
                <div className="account-field">
                  <label>Confirm New Password</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Re-enter password" />
                </div>
                <button type="submit" className="account-save" disabled={passBusy}>
                  {passBusy ? 'Updating…' : 'Update Password'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
